import { ChartData } from "chart.js";
import moment from "moment";
import { ChartMetric, NormalizedPowerRow } from "../types/power";

const metricLabels: Record<ChartMetric, string> = {
  power_active: "Power Active (W)",
  total_energy_active: "Total Energy (kWh)",
};

const chartPalette = [
  "#0b8e8b",
  "#f0a832",
  "#2d6cdf",
  "#33b679",
  "#e15959",
  "#6e58bd",
];

const getMetricValue = (
  row: NormalizedPowerRow,
  metric: ChartMetric,
): number | null => {
  const value = row[metric];
  return typeof value === "number" ? value : null;
};

export const getMetricLabel = (metric: ChartMetric) => metricLabels[metric];

export const buildRoomBarData = (
  rows: NormalizedPowerRow[],
  metric: ChartMetric,
): ChartData<"bar", number[], string> => {
  const summary = new Map<string, { total: number; count: number }>();

  rows.forEach((row) => {
    const value = getMetricValue(row, metric);
    if (!row.name || value === null) {
      return;
    }

    const current = summary.get(row.name) || { total: 0, count: 0 };
    summary.set(row.name, {
      total: current.total + value,
      count: current.count + 1,
    });
  });

  const sorted = Array.from(summary.entries()).sort(
    (a, b) => b[1].total - a[1].total,
  );

  const labels = sorted.map(([room]) => room);
  const data = sorted.map(([_, values]) =>
    values.count ? Number((values.total / values.count).toFixed(2)) : 0,
  );

  return {
    labels,
    datasets: [
      {
        label: `${getMetricLabel(metric)} (avg)`,
        data,
        backgroundColor: labels.map(
          (_, index) => chartPalette[index % chartPalette.length],
        ),
        borderRadius: 12,
      },
    ],
  };
};

export const buildRoomLineData = (
  rows: NormalizedPowerRow[],
  metric: ChartMetric,
): ChartData<"line", (number | null)[], string> => {
  if (!rows.length) {
    return { labels: [], datasets: [] };
  }

  const sorted = [...rows].sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  const timestamps = Array.from(new Set(sorted.map((row) => row.created_at)));
  const recentTimestamps = timestamps.slice(-30);
  const labels = recentTimestamps.map((timestamp) =>
    moment(timestamp).format("MMM D, h:mm A"),
  );

  const roomNames = Array.from(new Set(sorted.map((row) => row.name))).slice(
    0,
    6,
  );
  const lookup = new Map<string, Map<string, number>>();

  sorted.forEach((row) => {
    const value = getMetricValue(row, metric);
    if (!row.name || value === null) {
      return;
    }

    if (!lookup.has(row.name)) {
      lookup.set(row.name, new Map());
    }

    lookup.get(row.name)?.set(row.created_at, value);
  });

  return {
    labels,
    datasets: roomNames.map((room, index) => ({
      label: room,
      data: recentTimestamps.map(
        (timestamp) => lookup.get(room)?.get(timestamp) ?? null,
      ),
      borderColor: chartPalette[index % chartPalette.length],
      backgroundColor: `${chartPalette[index % chartPalette.length]}33`,
      tension: 0.35,
      pointRadius: 2,
      spanGaps: true,
      fill: true,
    })),
  };
};

export { chartPalette };
